import React from 'react';
import { Helmet } from 'react-helmet-async';
import processAssessment from '../assets/process-assessment.jpg';
import processProgramDesign from '../assets/process-program-design.jpg';
import processSafety from '../assets/process-safety.jpg';
import processApplicationDiagram from '../assets/process-application-diagram.png';
import processFollowup from '../assets/process-followup.jpg';
import howweworkReport from '../assets/howwework-report.jpg';
import Section from './Section.js';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';

const Processes = () => {
  return (
    <div className='bg-custom-earth'>
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Our Process | Safe Herbicide Application | Boultbee</title>
            <meta name="description" content="See how we deliver compliant, technology-driven herbicide programs. GPS tracking, flow control, and certified applicators." />
            <link rel="canonical" href="https://www.weedmanager.com/processes" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Our Process | Safe Herbicide Application | Boultbee" />
            <meta property="og:description" content="See how we deliver compliant, technology-driven herbicide programs. GPS tracking, flow control, and certified applicators." />
            <meta property="og:url" content="https://www.weedmanager.com/processes" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-processes.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Our Process | Safe Herbicide Application | Boultbee" />
            <meta name="twitter:description" content="See how we deliver compliant, technology-driven herbicide programs. GPS tracking, flow control, and certified applicators." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-processes.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Processes", "item": "https://www.weedmanager.com/processes" }
                ]
              })}
            </script>
          </Helmet>

          <PageHero
            title="Our Process"
            subtitle="How we deliver compliant, technology-driven herbicide programs"
            backgroundImage={howweworkReport}
          />

          {/* Step 1: Site Assessment */}
          <AnimateOnScroll animation="fade-in-up">
          <Section
            heading="Step 1: Site Assessment & Consultation"
            paragraph="Depending on location and scope, we assess your site using satellite imagery and aerial photos, a detailed phone consultation, or an on-site visit by a certified applicator. We identify target species, evaluate terrain and proximity to sensitive areas, review access and safety requirements, and understand your operational constraints. This means we arrive on day one with the right equipment, products, and plan — whether your site is an hour away or nine."
            imgSrc={processAssessment}
            altText="Boultbee technician conducting GPS site assessment for industrial herbicide application"
            imgWidth={1200}
            imgHeight={630}
          />
          </AnimateOnScroll>

          {/* Step 2: Program Design */}
          <AnimateOnScroll animation="fade-in-up">
          <Section
            heading="Step 2: Program Design & Herbicide Selection"
            paragraph="Based on the site assessment, we develop a custom herbicide program specifying products, application rates, timing, and methods. Product selection follows IPM principles: the most targeted, effective herbicide at the lowest effective rate. All products are registered for use in Canada under the Pest Control Products Act."
            imgSrc={processProgramDesign}
            altText="Satellite imagery and program planning for herbicide application design"
            imgWidth={1024}
            imgHeight={768}
          />
          </AnimateOnScroll>

          {/* Step 3: Safety & Compliance */}
          <AnimateOnScroll animation="fade-in-up">
          <Section
            heading="Step 3: Safety & Compliance Preparation"
            paragraph="Before application begins, we complete all required safety documentation including WorkSafeBC compliance checks, Pest Management Plan verification, client-specific safety orientations, and any required Avetta or ISNetworld documentation. For sensitive sites, we prepare environmental protection plans addressing water bodies, wildlife, and drift mitigation."
            imgSrc={processSafety}
            altText="Safety compliance and WHMIS hazard communication for herbicide application"
            imgWidth={1000}
            imgHeight={667}
          />
          </AnimateOnScroll>

          {/* Step 4: Precision Application */}
          <AnimateOnScroll animation="fade-in-up">
          <Section
            heading="Step 4: Precision Application"
            paragraph="Application is performed by certified technicians using GPS-tracked, flow-controlled spray equipment. Real-time monitoring ensures consistent delivery rates across the entire treatment area. High-rail vehicles are used for railroad applications. Specialized hand equipment is deployed for infrastructure targets like sidewalks, brickwork, and cable strips."
            imgSrc={processApplicationDiagram}
            altText="Boultbee herbicide injection system diagram showing flow-controlled precision application equipment"
            imgWidth={1232}
            imgHeight={864}
          />
          </AnimateOnScroll>

          {/* Step 5: Follow-Up */}
          <AnimateOnScroll animation="fade-in-up">
          <Section
            heading="Step 5: Follow-Up & Monitoring"
            paragraph="For noxious weed programs, we schedule follow-up inspections to assess efficacy and determine if additional treatment is needed. Monitoring informs future program adjustments to improve results season over season. You receive detailed invoicing with line items and area descriptions for every treatment."
            imgSrc={processFollowup}
            altText="Technician monitoring previously treated site for vegetation regrowth and treatment efficacy"
            imgWidth={1200}
            imgHeight={630}
          />
          </AnimateOnScroll>

        </div>
      </div>
    </div>
  );
}

export default Processes;
